import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiurl from "../Api";

const REVIEW_PASSWORD = "divyanshu1364";
const SESSION_KEY = "cases1364_review_unlocked";

function HiddenCasesReview() {
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(() => sessionStorage.getItem(SESSION_KEY) === "yes");
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [viewMode, setViewMode] = useState("cases");
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 1,
  });

  useEffect(() => {
    document.title = "Cases Review | MERAQUI";
  }, []);

  const fetchCases = useCallback(async (nextPage = page, nextPageSize = pageSize, nextSearch = appliedSearch) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const params = new URLSearchParams({
        subdomain: "react",
        action: "review_cases_all",
        page: String(nextPage),
        page_size: String(nextPageSize),
      });
      if (nextSearch.trim()) {
        params.set("search", nextSearch.trim());
      }

      const response = await axios.get(`${apiurl}registeration.php?${params.toString()}`);
      if (response.data?.status === true) {
        setCases(response.data.cases || []);
        setPagination(response.data.pagination || {
          page: nextPage,
          page_size: nextPageSize,
          total: 0,
          total_pages: 1,
        });
      } else {
        setCases([]);
        setErrorMsg(response.data?.message || "Unable to load cases.");
      }
    } catch (error) {
      setCases([]);
      setErrorMsg("Unable to load cases.");
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, page, pageSize]);

  useEffect(() => {
    if (!isUnlocked) return;
    fetchCases(page, pageSize, appliedSearch);
  }, [appliedSearch, fetchCases, isUnlocked, page, pageSize]);

  const totalPages = Math.max(1, Number(pagination.total_pages || 1));
  const totalCases = Number(pagination.total || 0);
  const hasActiveSearch = appliedSearch.trim() !== "";
  const filteredCases = useMemo(() => {
    return cases.filter((item) => {
      const isImported = Number(item.imported_candidate_id || 0) > 0 || item.candidate_import_status === "added";
      return viewMode === "added" ? isImported : !isImported;
    });
  }, [cases, viewMode]);

  const pageNumbers = useMemo(() => {
    const items = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    for (let current = start; current <= end; current += 1) {
      items.push(current);
    }
    return items;
  }, [page, totalPages]);

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightText = (value) => {
    const text = value === null || value === undefined || value === "" ? "-" : String(value);
    const term = appliedSearch.trim();
    if (!term) return text;

    const regex = new RegExp(`(${escapeRegExp(term)})`, "ig");
    const parts = text.split(regex);
    const lowerTerm = term.toLowerCase();

    return parts.map((part, index) => (
      part.toLowerCase() === lowerTerm ? (
        <mark
          key={`${text}-${index}`}
          style={{
            backgroundColor: "#fff1a8",
            color: "#1e2a44",
            padding: "0 2px",
            borderRadius: "3px",
          }}
        >
          {part}
        </mark>
      ) : (
        <React.Fragment key={`${text}-${index}`}>{part}</React.Fragment>
      )
    ));
  };

  const handleUnlock = (event) => {
    event.preventDefault();
    if (password !== REVIEW_PASSWORD) {
      setPasswordError("Incorrect password.");
      return;
    }
    sessionStorage.setItem(SESSION_KEY, "yes");
    setPasswordError("");
    setIsUnlocked(true);
  };

  const handleLock = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setIsUnlocked(false);
    setPassword("");
    setCases([]);
    setErrorMsg("");
    setPage(1);
    setSearchInput("");
    setAppliedSearch("");
    setViewMode("cases");
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setAppliedSearch("");
    setPage(1);
  };

  if (!isUnlocked) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow-sm border-0">
              <div className="card-body p-4">
                <h3 className="mb-2">Cases Review Access</h3>
                <p className="text-muted mb-4">Enter the password to open the hidden cases review page.</p>
                <form onSubmit={handleUnlock}>
                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                    />
                  </div>
                  {passwordError ? <div className="alert alert-danger py-2">{passwordError}</div> : null}
                  <button type="submit" className="btn btn-primary w-100">
                    Open Review Page
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="container-fluid py-4 px-3 px-md-4"
      style={{
        background: "linear-gradient(180deg, #f4f7ff 0%, #f8fbff 40%, #ffffff 100%)",
        minHeight: "calc(100vh - 70px)",
      }}
    >
      <div className="mx-auto" style={{ maxWidth: "1600px" }}>
        <div className="card border-0 shadow-sm" style={{ borderRadius: "20px", overflow: "hidden" }}>
          <div className="card-body p-3 p-md-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
              <div>
                <div className="fw-bold fs-4 text-dark">Cases Review</div>
                <div className="text-muted small mb-1">
                  Review all mapped Lineup and Campaign cases with fully flattened OCR leaf fields and global search.
                </div>
                <div className="fw-semibold text-dark">Total Cases: {totalCases}</div>
                <div className="text-muted small">
                  Page {page} of {totalPages}
                  {hasActiveSearch ? ` • Search: "${appliedSearch}"` : ""}
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 align-items-center">
                <div className="btn-group" role="group" aria-label="Case filter toggle">
                  <button
                    type="button"
                    className={`btn ${viewMode === "cases" ? "btn-primary" : "btn-outline-primary"}`}
                    style={{ borderRadius: "12px 0 0 12px" }}
                    onClick={() => setViewMode("cases")}
                  >
                    Cases
                  </button>
                  <button
                    type="button"
                    className={`btn ${viewMode === "added" ? "btn-success" : "btn-outline-success"}`}
                    style={{ borderRadius: "0 12px 12px 0" }}
                    onClick={() => setViewMode("added")}
                  >
                    Added Candidates
                  </button>
                </div>
                <select
                  className="form-select"
                  style={{ width: "140px", borderRadius: "12px" }}
                  value={pageSize}
                  onChange={(e) => {
                    const nextSize = Number(e.target.value || 10);
                    setPageSize(nextSize);
                    setPage(1);
                  }}
                >
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                </select>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => fetchCases(page, pageSize, appliedSearch)}
                  style={{ borderRadius: "12px", minWidth: "110px" }}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={handleLock}
                  style={{ borderRadius: "12px", minWidth: "110px" }}
                >
                  Lock
                </button>
              </div>
            </div>

            <form onSubmit={handleSearchSubmit} className="row g-2 align-items-center mb-4">
              <div className="col-12 col-lg-8">
                <input
                  type="text"
                  className="form-control"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search candidate, case ID, requirement, mobile, proceed to, document type, file name, OCR keys or OCR values"
                  style={{
                    borderRadius: "14px",
                    minHeight: "48px",
                    borderColor: "#d8e2ff",
                    boxShadow: "none",
                  }}
                />
              </div>
              <div className="col-6 col-lg-2">
                <button
                  type="submit"
                  className="btn w-100"
                  style={{
                    minHeight: "48px",
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, #1f3c88 0%, #3d68d9 100%)",
                    color: "#ffffff",
                    fontWeight: 600,
                  }}
                >
                  Search
                </button>
              </div>
              <div className="col-6 col-lg-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100"
                  onClick={handleClearSearch}
                  style={{ minHeight: "48px", borderRadius: "14px" }}
                >
                  Clear
                </button>
              </div>
            </form>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : errorMsg ? (
              <div className="alert alert-danger mb-0">{errorMsg}</div>
            ) : filteredCases.length === 0 ? (
              <div className="alert alert-info mb-0">
                {viewMode === "added" ? "No added candidates found for the current search." : "No pending cases found for the current search."}
              </div>
            ) : (
              <div
                style={{
                  border: "1px solid #e6ecff",
                  borderRadius: "18px",
                  overflow: "hidden",
                  backgroundColor: "#ffffff",
                }}
              >
                <div
                  style={{
                    overflowX: "auto",
                    overflowY: "auto",
                    maxHeight: "86vh",
                    width: "100%",
                  }}
                >
                  <table className="table align-middle mb-0" style={{ minWidth: "1980px" }}>
                    <thead
                      className="sticky-top"
                      style={{
                        background: "linear-gradient(180deg, #eef3ff 0%, #f8faff 100%)",
                        zIndex: 2,
                      }}
                    >
                      <tr>
                        <th className="px-3 py-3" style={{ minWidth: "70px", backgroundColor: "#eef3ff" }}>#</th>
                        <th className="px-3 py-3" style={{ minWidth: "220px", backgroundColor: "#eef3ff" }}>Candidate</th>
                        <th className="px-3 py-3" style={{ minWidth: "210px", backgroundColor: "#eef3ff" }}>Case ID</th>
                        <th className="px-3 py-3" style={{ minWidth: "260px", backgroundColor: "#eef3ff" }}>Requirement</th>
                        <th className="px-3 py-3" style={{ minWidth: "130px", backgroundColor: "#eef3ff" }}>Proceed To</th>
                        <th className="px-3 py-3" style={{ minWidth: "150px", backgroundColor: "#eef3ff" }}>Mobile</th>
                        <th className="px-3 py-3 text-center" style={{ minWidth: "120px", backgroundColor: "#eef3ff" }}>Documents</th>
                        <th className="px-3 py-3 text-center" style={{ minWidth: "130px", backgroundColor: "#eef3ff" }}>Leaf Fields</th>
                        <th className="px-3 py-3" style={{ minWidth: "220px", backgroundColor: "#eef3ff" }}>Mapped By</th>
                        <th className="px-3 py-3" style={{ minWidth: "190px", backgroundColor: "#eef3ff" }}>Last Updated</th>
                        <th className="px-3 py-3" style={{ minWidth: "680px", backgroundColor: "#eef3ff" }}>Review Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCases.map((item, index) => {
                        const isImported = Number(item.imported_candidate_id || 0) > 0 || item.candidate_import_status === "added";
                        return (
                        <tr
                          key={item.case_id || item.id || index}
                          style={{
                            borderTop: "1px solid #edf1fb",
                            backgroundColor: isImported ? "#eaf8ef" : undefined,
                          }}
                        >
                          <td className="px-3 py-3 text-muted">{(page - 1) * pageSize + index + 1}</td>
                          <td className="px-3 py-3 fw-semibold">{highlightText(item.candidate_name)}</td>
                          <td className="px-3 py-3">{highlightText(item.case_id)}</td>
                          <td className="px-3 py-3">
                            <div className="fw-semibold text-dark">
                              {highlightText(item.requirement_rid || item.requirement_aid || "-")}
                            </div>
                            <div className="small text-muted">
                              {highlightText(item.requirement_client_name || "-")}
                            </div>
                            <div className="small text-muted">
                              {highlightText(item.requirement_job_title || item.requirement_designation || "-")}
                              {item.requirement_location ? <> {" - "}{highlightText(item.requirement_location)}</> : null}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className="badge"
                              style={{
                                backgroundColor: isImported ? "#d9f2e0" : (item.proceed_to === "Campaign" ? "#fff1d6" : "#e7f0ff"),
                                color: isImported ? "#17633a" : (item.proceed_to === "Campaign" ? "#8a5a00" : "#173c88"),
                                padding: "8px 12px",
                                borderRadius: "999px",
                                fontWeight: 600,
                              }}
                            >
                              {highlightText(item.proceed_to)}
                            </span>
                          </td>
                          <td className="px-3 py-3">{highlightText(item.candidate_mobile)}</td>
                          <td className="px-3 py-3 text-center fw-semibold">{item.docs_count ?? 0}</td>
                          <td className="px-3 py-3 text-center fw-semibold">{item.leaf_field_count ?? 0}</td>
                          <td className="px-3 py-3">
                            <div className="fw-semibold">{highlightText(item.mapped_by_name)}</div>
                            <small className="text-muted">{highlightText(item.mapped_by_post)}</small>
                          </td>
                          <td className="px-3 py-3 text-nowrap">{highlightText(item.last_mapped_at || item.created_at)}</td>
                          <td className="px-3 py-3">
                            {Array.isArray(item.documents) && item.documents.length > 0 ? (
                              <div className="d-flex flex-column gap-2">
                                {item.documents.map((doc, docIndex) => (
                                  <details
                                    key={doc.document_id || docIndex}
                                    style={{
                                      border: "1px solid #dfe6fb",
                                      borderRadius: "14px",
                                      backgroundColor: "#f8fbff",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <summary
                                      className="fw-semibold"
                                      style={{
                                        cursor: "pointer",
                                        padding: "12px 14px",
                                        backgroundColor: "#f3f7ff",
                                      }}
                                    >
                                      {highlightText(doc.document_type || `Document ${docIndex + 1}`)}
                                      {doc.file_name ? <> {" - "}{highlightText(doc.file_name)}</> : null}
                                      {` (${doc.field_count || 0} fields)`}
                                    </summary>
                                    <div className="p-3 d-flex flex-column gap-2 small">
                                      {Array.isArray(doc.fields) && doc.fields.length > 0 ? (
                                        doc.fields.map((field, fieldIndex) => (
                                          <div
                                            key={`${doc.document_id || docIndex}-${field.key || "field"}-${fieldIndex}`}
                                            style={{
                                              padding: "10px 12px",
                                              borderRadius: "10px",
                                              backgroundColor: "#ffffff",
                                              border: "1px solid #eef2ff",
                                            }}
                                          >
                                            <strong style={{ color: "#163475" }}>{highlightText(field.key || "-")}</strong>
                                            <span>: {highlightText(field.value || "-")}</span>
                                          </div>
                                        ))
                                      ) : (
                                        <span className="text-muted">No extracted fields</span>
                                      )}
                                    </div>
                                  </details>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">No document data</span>
                            )}
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap justify-content-center align-items-center gap-2 mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={page <= 1 || loading}
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            style={{ borderRadius: "12px", minWidth: "110px" }}
          >
            Previous
          </button>
          {pageNumbers.map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              className={`btn ${pageNumber === page ? "btn-primary" : "btn-outline-secondary"}`}
              disabled={loading}
              onClick={() => setPage(pageNumber)}
              style={{ borderRadius: "12px", minWidth: "48px" }}
            >
              {pageNumber}
            </button>
          ))}
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            style={{ borderRadius: "12px", minWidth: "110px" }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default HiddenCasesReview;
